import {
  Anchor,
  Card,
  Figure,
  Heading,
  Inline,
  Separator,
  Stack,
  Text,
} from "@generalapplications/ui";

import { PostByline } from "@/app/features/blog/components/PostByline";
import { ResponsiveImage } from "@/app/common/components/composite/media/ResponsiveImage";
import { StatusBadge } from "@/app/common/components/composite/feedback/StatusBadge";
import { Tag } from "@/app/common/components/composite/feedback/Tag";
import type { BlogPostSummary } from "@/content/blog";
import { routes } from "@/app/common/functions/routes/routes";

const statusNote = {
  idea: "Editorial idea — this article is hidden in production.",
  draft: "Draft preview — this article is hidden in production.",
  ready: "Ready for release — intended target date.",
} as const;

export type ArticleHeaderProps = {
  post: BlogPostSummary;
};

/** An article's byline, title, tags and picture. */
export function ArticleHeader({ post }: ArticleHeaderProps) {
  return (
    <Stack gap="lg">
      <Inline wrap gap="sm">
        {post.status !== "published" && (
          <StatusBadge status={post.status} title={statusNote[post.status]} />
        )}
        <Text variant="secondary">
          By{" "}
          <Anchor href={routes.about} tone="inherit" weight="semibold">
            General Applications
          </Anchor>
          {" · "}
          <PostByline post={post} showUpdated />
        </Text>
      </Inline>

      <Heading level={1} size="xxl">
        {post.title}
      </Heading>

      <Inline wrap gap="xs">
        {post.tags.map((tag) => (
          <Tag key={tag}>{tag}</Tag>
        ))}
      </Inline>

      {post.image && (
        // The lede figure, wide: a card with no padding is the picture's
        // rounded, ruled frame.
        <Figure wide>
          <Card padding="none" radius="xl">
            <ResponsiveImage
              src={post.image}
              width={1200}
              height={630}
              ratio="16:9"
              eager
            />
          </Card>
        </Figure>
      )}

      <Separator />
    </Stack>
  );
}
